import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  TextField, 
  Button, 
  Typography, 
  Box, 
  CircularProgress, 
  InputAdornment, 
  IconButton,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock, PhoneCallback } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { authService } from '../utils/api';

function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [checkingToken, setCheckingToken] = useState(true);

  // Check for existing token on component mount
  useEffect(() => {
    const checkExistingToken = async () => {
      const token = localStorage.getItem('token');
      
      if (token) {
        try {
          // Validate token with /me endpoint
          await authService.getCurrentUser();
          // If successful, redirect to assistants
          navigate('/assistants');
        } catch (error) {
          // Token is invalid or expired, remove it
          authService.logout();
          console.log('Token validation failed:', error.message);
        }
      }
      
      setCheckingToken(false);
    };

    checkExistingToken();
  }, [navigate]);

  const validateForm = () => {
    const newErrors = {};
    if (!email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Email is invalid';
    if (!password) newErrors.password = 'Password is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      await authService.login(email, password);
      navigate('/assistants');
    } catch (error) {
      toast.error(error.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading spinner while checking token
  if (checkingToken) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: 'background.default',
        padding: { xs: 2, md: 0 }
      }}
    >
      <Grid container sx={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Left side - Brand information */}
        <Grid 
          item 
          xs={0} 
          md={6} 
          sx={{ 
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            padding: 4
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              color: 'white',
              textAlign: 'center',
              borderRadius: 3,
              overflow: 'hidden',
              backgroundColor: 'primary.dark',
              padding: 4,
            }}
          >
            {/* F2Fintech Logo - Top Right */}
            <Box
              component="img"
              src="https://f2fintech.com/f2Fintechlogo-old.png"
              alt="F2Fintech Logo"
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                width: 'auto',
                height: '125px',
                objectFit: 'contain',
                zIndex: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                borderRadius: 2,
                padding: 1,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
            />
            <Box
              component="img"
              src="/telly_logo.png"
              alt="Telly Logo"
              sx={{
                width: 'auto',
                height: { md: '102px', lg: '119px' },
                mb: 2,
                objectFit: 'contain'
              }}
            />
            
            <Typography variant="h5" sx={{ mb: 4, fontWeight: 400 }}>
              The super-support agent AI 
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
              <PhoneCallback fontSize="large" />
              <Typography variant="h6" sx={{ fontWeight: 400 }}>
                AI that answers. AI that reaches out.
              </Typography>
            </Box>
            
            <Box 
              sx={{ 
                p: 3, 
                backgroundColor: 'rgba(255, 255, 255, 0.1)', 
                borderRadius: 2,
                backdropFilter: 'blur(10px)',
                width: '100%',
                maxWidth: '480px' 
              }}
            >
              <Typography variant="body1" paragraph>
                Handle 1,000+ concurrent calls with human-like tone and zero script fatigue.
              </Typography>
              <Typography variant="body1">
                The only AI super agent for inbound & outbound calls that speaks like you & me.
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Right side - Login form */}
        <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Card 
            sx={{ 
              width: '100%', 
              maxWidth: '450px',
              borderRadius: 3,
              boxShadow: 3,
              py: 3,
              px: { xs: 3, sm: 6 }
            }}
          >
            <CardContent>
              {/* Mobile logo */}
              <Box sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', mb: 4 }}>
                <Box
                  component="img"
                  src="/telly_logo.png"
                  alt="Telly Logo"
                  sx={{
                    width: 'auto',
                    height: '68px',
                    objectFit: 'contain'
                  }}
                />
              </Box>

              <Typography variant="h4" sx={{ mb: 4, textAlign: 'center', fontWeight: 600 }}>
                Welcome back
              </Typography>
              
              <form onSubmit={handleSubmit}>
                <Box sx={{ mb: 3 }}>
                  <TextField
                    label="Email"
                    variant="outlined"
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={!!errors.email}
                    helperText={errors.email}
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color="primary" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 3 }}
                  />
                  
                  <TextField
                    label="Password"
                    variant="outlined"
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    error={!!errors.password}
                    helperText={errors.password}
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="primary" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                
                <Box sx={{ textAlign: 'right', mb: 2 }}>
                  <Button
                    component={Link}
                    to="/forgot-password"
                    variant="text"
                    size="small"
                    sx={{ textTransform: 'none' }}
                  >
                    Forgot Password?
                  </Button>
                </Box>
                
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  disabled={loading}
                  sx={{ mt: 2, py: 1.5 }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                </Button>
              </form>
              
              <Divider sx={{ my: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  OR
                </Typography>
              </Divider>
              
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Don't have an account?
                </Typography>
                <Button 
                  component={Link} 
                  to="/signup" 
                  variant="outlined" 
                  color="primary"
                  fullWidth
                >
                  Create Account
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Login; 